import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { SimpleRadioGroup } from "@repo/ui/Radio/simpleRadioGroup";
import { SimpleRadioOption } from "@repo/ui/Radio/simpleRadioOption";
import { type FC, useState } from "react";
import { Dialog } from "react-aria-components";

export type OutcomeSource = "transcript" | "field";

interface OutcomeSourceChooserDialogProps {
  isOpen: boolean;
  currentSource: OutcomeSource;
  onOpenChange: (isOpen: boolean) => void;
  onChoose: (source: OutcomeSource) => void;
}

const sourceOptions: readonly {
  source: OutcomeSource;
  title: string;
  description: string;
}[] = [
  {
    source: "transcript",
    title: "Call transcript",
    description:
      "Alpharun reads the conversation and follows your instructions.",
  },
  {
    source: "field",
    title: "CRM field",
    description:
      "Alpharun uses a synced CRM field your team already maintains.",
  },
];

interface OutcomeSourceChooserDialogContentsProps {
  currentSource: OutcomeSource;
  onChoose: (source: OutcomeSource) => void;
  onClose: () => void;
}

const OutcomeSourceChooserDialogContents: FC<
  OutcomeSourceChooserDialogContentsProps
> = (props) => {
  const { currentSource, onChoose, onClose } = props;
  const [selectedSource, setSelectedSource] =
    useState<OutcomeSource>(currentSource);

  return (
    <>
      <ModalHeader onClose={onClose}>
        Change how the outcome is determined
      </ModalHeader>

      <div className="mt-4">
        <SimpleRadioGroup
          value={selectedSource}
          onChange={(source) => setSelectedSource(source as OutcomeSource)}
        >
          <div className="space-y-5">
            {sourceOptions.map((option) => (
              <div key={option.source}>
                <SimpleRadioOption value={option.source}>
                  {option.title}
                </SimpleRadioOption>
                <p className="mt-1 ml-6 text-xs leading-5 text-muted-foreground">
                  {option.description}
                </p>
              </div>
            ))}
          </div>
        </SimpleRadioGroup>
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button variant={ButtonVariant.Secondary} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          isDisabled={selectedSource === currentSource}
          onClick={() => {
            onClose();
            onChoose(selectedSource);
          }}
        >
          Change source
        </Button>
      </div>
    </>
  );
};

/** A deliberate source change flow; changing source can replace its setup. */
export const OutcomeSourceChooserDialog: FC<OutcomeSourceChooserDialogProps> = (
  props,
) => {
  const { isOpen, currentSource, onOpenChange, onChoose } = props;

  return (
    <CenteredModal
      isDismissable
      isOpen={isOpen}
      size={Size.SMALL}
      onOpenChange={onOpenChange}
    >
      <Dialog className="w-full max-w-lg px-4 pt-5 pb-4 text-left outline-hidden sm:p-6">
        {({ close }) => (
          <OutcomeSourceChooserDialogContents
            currentSource={currentSource}
            onChoose={onChoose}
            onClose={close}
          />
        )}
      </Dialog>
    </CenteredModal>
  );
};
